import ImageBlock from '@/components/imageBlock'
import { BRANDED_FARE_FAMILY } from '@/constants/Srp'
import { replacePlaceholders } from '@/utils'
import { deepClone, isEmpty } from '@/utils/general/browserHelper'
import { getFormattedDate } from '@/utils/general/date'
import { getFlightLogoUrl } from '@/utils/images'
import { validatePTC } from '@/utils/srp'
import { Container, Typography } from '@cleartrip/ct-design-components'
import dayjs from 'dayjs'
import insuranceTestimonialsData from '@/assets/Jsons/InsuranceTestimonials.json'
import { getCookie } from '@/utils/general/cookie'
import { getUserAuthValues } from '@/utils/general/user'
import { AMEND_INSURANCE, DOM_CANCELATION_INSURANCE, INTL_CANCELATION_INSURANCE } from './AddOnCards/fareCards'
import { PTC_FARE_CATEGORY } from '@/constants/components/fareType'

const BENEFIT_TYPES = {
  AMEND: 'AMEND',
  CANCEL: 'CANCEL',
  CHECK_IN_BAGGAGE: 'CHECK_IN_BAGGAGE',
  HAND_BAGGAGE: 'HAND_BAGGAGE',
  SEAT: 'SEAT',
  MEAL: 'MEAL',
} as const
const DEFAULT_VALUE = 'null'
const SEPARATOR = ' | '
export const INFO_NOT_AVAILABLE = 'Info not available'

export const getFlightDuration = (duration = 0) => {
  const totalMinutes = Math.floor(duration / 60)
  const totalHours = Math.floor(totalMinutes / 60)
  const days = Math.floor(totalHours / 24)
  const hours = totalHours % 24
  const minutes = totalMinutes % 60
  return {
    days,
    hours,
    minutes,
  }
}

export const getDurationText = (days = 0, hours = 0, minutes = 0) => {
  const daysText = days > 0 ? `${days}d ` : ''
  const hoursText = hours > 0 ? `${hours}h ` : ''
  const minutesText = minutes > 0 ? `${minutes}m` : ''
  return daysText + hoursText + minutesText
}

const processFareCards = fareCards => {
  if (!fareCards?.length) return ''

  const selectedCard = fareCards.find(card => card?.selected)
  if (!selectedCard?.supplyBenefitsList?.benefitSummary?.length) return ''

  return selectedCard.supplyBenefitsList.benefitSummary
    .map(benefit => {
      const { text, placeholderMap } = benefit || {}
      return text ? replacePlaceholders(text, placeholderMap || {}) : ''
    })
    .filter(text => text)
    .join(SEPARATOR)
}

export const getFareRuleBaggageText = (onwardFareCards, returnFareCards) => {
  const onwardText = processFareCards(onwardFareCards)
  const returnText = processFareCards(returnFareCards)
  return { onwardText, returnText }
}

export const getAmendCancelData = (selectedFare: any) => {
  let { benefits: { penalty = [] } = {} } = selectedFare || {}
  const amendText = getFareRuleTextData(penalty, BENEFIT_TYPES.AMEND) || DEFAULT_VALUE
  const cancelText = getFareRuleTextData(penalty, BENEFIT_TYPES.CANCEL) || DEFAULT_VALUE
  return { amendText, cancelText }
}

export const getBaggagesData = (searchData: any, selectedFare: any) => {
  const { baggageAllowances = {} } = searchData || {}
  let { benefits: { baggageAllowanceId = '' } = {} } = selectedFare || {}
  let baggageData = baggageAllowances?.[baggageAllowanceId] || {}
  let checkInBaggageData = ''
  let cabinBaggageData = ''
  baggageData?.benefitSummary?.forEach((baggage: any) => {
    if (baggage?.benefitType === BENEFIT_TYPES.CHECK_IN_BAGGAGE) {
      checkInBaggageData = replacePlaceholders(baggage?.text, baggage?.placeholderMap) || DEFAULT_VALUE
    } else if (baggage?.benefitType === BENEFIT_TYPES.HAND_BAGGAGE) {
      cabinBaggageData = replacePlaceholders(baggage?.text, baggage?.placeholderMap) || DEFAULT_VALUE
    }
  })
  return { checkInBaggageData, cabinBaggageData }
}

export const getSeatMealData = (searchData: any, selectedFare: any) => {
  const { benefits = {} } = searchData || {}
  let { benefits: { benefitIds = [] } = {} } = selectedFare || {}
  let seatData = ''
  let mealData = ''
  benefitIds?.map((benefitId: any) => {
    const benefitData = benefits?.[benefitId]?.benefitSummary?.[0]
    if (benefitData?.benefitType === BENEFIT_TYPES.SEAT) {
      seatData = benefitData?.text || DEFAULT_VALUE
    } else if (benefitData?.benefitType === BENEFIT_TYPES.MEAL) {
      mealData = benefitData?.text || DEFAULT_VALUE
    }
  })
  return { seatData, mealData }
}

export const getFareRuleTextData = (penalty = [], type = '') => {
  let fareRuleText = ''
  penalty.forEach((penaltyEle: any) => {
    const fareRuleBenefit = penaltyEle?.benefitSummary?.filter((ele: any) => ele.benefitType === type)?.[0]
    if (!isEmpty(fareRuleBenefit)) {
      fareRuleText = replacePlaceholders(fareRuleBenefit?.text, fareRuleBenefit?.placeholderMap)
    }
  })
  return fareRuleText
}

export const isArrivesNexyDay = (flight: any) => {
  const { segments = [] } = flight || {}
  const lastIndex = segments?.length - 1
  const firstDepartDate = getFormattedDate(segments[0]?.formattedDepartDateTime, 'DD', 'YYYY-MM-DDTHH:mm:ss')
  const lastArrivalDate = getFormattedDate(segments[lastIndex]?.formattedArrivalDateTime, 'DD', 'YYYY-MM-DDTHH:mm:ss')
  return firstDepartDate !== lastArrivalDate
}

export const getFlightNumbers = (segments: any[]) => {
  const flightNumbers: string[] = []
  segments.forEach(segment => {
    const { airline = '', flightNumber = '' } = segment || {}
    flightNumbers.push(`${airline}-${flightNumber}`)
  })
  return flightNumbers
}

export const getStopsText = (stops: number) => {
  if (stops === 0) return 'Non stop'
  else return `${stops} stop`
}

export const getTotalFlightDuration = (flightData: any) => {
  let totalFlightDuration = flightData?.totalDuration || 0
  if (flightData?.layOverDurations?.length) {
    totalFlightDuration += flightData.layOverDurations.reduce((a: any, b: any) => {
      return a + b
    })
  }
  return totalFlightDuration
}

export const getAirlineLogos = (airlines: any, isMultiAirline: boolean, height = 28, width = 28) => {
  //remove repeated airlines
  const nonRepeatedAirlines: any[] = []
  airlines.map((airline: { code: any }) => {
    if (nonRepeatedAirlines.filter(nonRepeatedAirline => nonRepeatedAirline.code === airline.code).length <= 0) {
      nonRepeatedAirlines.push(airline)
    }
  })
  //get airline logos
  let logos
  if (isMultiAirline) {
    logos =
      nonRepeatedAirlines.length > 2 ? (
        <Container className='flex'>
          <ImageBlock
            path={getFlightLogoUrl(nonRepeatedAirlines[0]?.code, 'circle_logo')}
            height={height}
            width={width}
            altText={nonRepeatedAirlines[0]?.code}
            isCircleLogo={true}
          />
          <Container
            backgroundColor='#F3F3F3'
            className={'flex flex-middle ws-nowrap'}
            style={{ marginLeft: '-12px', padding: '6px', borderRadius: '40px' }}
          >
            <Typography variant='B3' color='subheading'>
              {' '}
              {`+${nonRepeatedAirlines.length - 1}`}
            </Typography>
          </Container>
        </Container>
      ) : (
        nonRepeatedAirlines.map((airline: { code: any }, index: number) => {
          return (
            <Container className='flex flex-center flex-middle' style={index > 0 ? { marginLeft: '-12px' } : {}}>
              {
                <ImageBlock
                  path={getFlightLogoUrl(airline?.code, 'circle_logo')}
                  height={height}
                  width={width}
                  altText={nonRepeatedAirlines[0]?.code}
                  isCircleLogo={true}
                />
              }
            </Container>
          )
        })
      )
  } else {
    logos = (
      <ImageBlock
        path={getFlightLogoUrl(nonRepeatedAirlines[0]?.code, 'circle_logo')}
        height={height}
        width={width}
        altText={nonRepeatedAirlines[0]?.code}
        isCircleLogo={true}
      />
    )
  }
  // get airline name for logo
  const logoText = isMultiAirline ? 'Multiple' : nonRepeatedAirlines[0]?.name

  return { logos, logoText }
}

export const getAirlines = (segments: any = []) => {
  //get airline names and codes
  const airlines: { name: any; code: any }[] = []
  segments.map((segment: { airlineName: any; airline: any }) => {
    airlines.push({ name: segment.airlineName, code: segment.airline })
  })
  return airlines
}

export const isRegularFareSelectedOnward = (fareCardsDataCFW: any = []) =>
  fareCardsDataCFW?.onwardFareCards?.some(card => card?.fareCardType === 'REGULAR_FARE' && card?.selected)

export const isRegularFareSelectedReturn = (fareCardsDataCFW: any = []) =>
  fareCardsDataCFW?.returnFareCards?.some(card => card?.fareCardType === 'REGULAR_FARE' && card?.selected)

export const isBrandedFareFamilyFareCardSelectedOnward = (fareCardsDataCFW: any = []) =>
  fareCardsDataCFW?.onwardFareCards?.some(
    (card: any) => card?.fareCardType === BRANDED_FARE_FAMILY && card?.selected
  )

export const isBrandedFareFamilyFareCardSelectedReturn = (fareCardsDataCFW: any = []) =>
  fareCardsDataCFW?.returnFareCards?.some(
    (card: any) => card?.fareCardType === BRANDED_FARE_FAMILY && card?.selected
  )

export const getSelectedFare = (fareCards: any, index: number) => {
  const { onwardFareCards = [], returnFareCards = [], tripFareCards = [] } = fareCards || {}
  const isSplRt = !isEmpty(tripFareCards)
  const jounreyTypeCards = isSplRt ? tripFareCards : index === 0 ? onwardFareCards : returnFareCards
  const selectedCard = jounreyTypeCards?.filter?.((card: any) => card?.selected)?.[0]
  const selectedFareName = selectedCard?.fareCardMessage?.title || ''
  return selectedFareName
}

export const getSelectedFareDetails = (fareCards: any) => {
  const { onwardFareCards = [], returnFareCards = [] } = fareCards || {}
  return {
    selectedOnewayCard: onwardFareCards?.find((card: any) => card?.selected),
    selectedRoundTripCard: returnFareCards?.find((card: any) => card?.selected),
  }
}

export const sortArrayOfObjects = (arr : any = [], key = '') => {
    let sortedArray = arr.sort((item1: any, item2: any) => {
        if (isNaN(item1[key])) {
            return 0
        }
        if (isNaN(item2[key])) {
            return -1
        }
        return item1[key] - item2[key]
    })
    return sortedArray
}
interface Benefit {
    iconUrl: string
    title: string
}
export const getBenefits = (benefitGroups: any) => {
    let benefits: Partial<Benefit>[] = []
    benefitGroups.forEach((group: any) => {
        if (!isEmpty(group.benefits)) {
            benefits.push(...group.benefits)
        }
    })
    let benefitArray = sortArrayOfObjects(benefits, 'rank')
    // benefitArray = benefitArray.slice(0, 11)
    return benefitArray
}

export const getInsurancePayloadData = (insuranceDetails: any, customDays: number | null = null, insuranceOpted: boolean = false , insuranceType: string = '' , daysCount: number = 0 , itineraryData: any = {}) => {
    const { endDate: endDateOld, startDate: startDateOld } = insuranceDetails[0] || {}
    const isDurationUpdate = customDays !== null
    let operationType = ''
    if (isDurationUpdate) {
        operationType = 'ADD'
    } else {
        operationType = insuranceOpted ? 'REMOVE' : 'ADD'
    }
    let finalStartDate = null
    let finalEndDate = null
    if (operationType === 'ADD' && insuranceType === 'INTL_TRAVEL_INSURANCE') {
        // INTERNATIONAL: Calculate dates based on flight departure + days
        const daysToUse = customDays || daysCount
        const flightData = (itineraryData as any)?.FLIGHT || {}
        const { flights = [] } = flightData || {}
        const firstFlight = flights?.[0] || {}
        const { segments = [] } = firstFlight || {}
        const firstSegment = segments?.[0] || {}
        const flightDepartureDate = firstSegment?.formattedDepartDateTime
        
        if (flightDepartureDate) {
            const startDateIntl = dayjs(flightDepartureDate).format('YYYY-MM-DD')
            const endDateIntl = dayjs(flightDepartureDate).add(daysToUse - 1, 'day').format('YYYY-MM-DD')
            
            finalStartDate = startDateIntl
            finalEndDate = endDateIntl
            
        } else {
            finalStartDate = startDateOld
            finalEndDate = endDateOld
        }
    } else {
        // DOMESTIC: Use existing dates (usually null) OR when removing insurance
        finalStartDate = startDateOld
        finalEndDate = endDateOld
    }
    return { finalStartDate, finalEndDate, operationType }
}
export const getSelectedFareCardCfw = (FARE_CARDS: any) => {
  const ow = FARE_CARDS?.onwardFareCards?.find(fareCard => {
    return fareCard?.selected === true
  })
  const rt = FARE_CARDS?.returnFareCards?.find(fareCard => {
    return fareCard?.selected === true
  })
  return { ow, rt }
}

export const getSegmentBenefits = (benefits: any, arrivalAirportIata: string) => {
  if (!benefits?.segmentFareBenefits) {
    return []
  }
  const { segmentFareBenefits = [] } = benefits
  const matchingBenefit = segmentFareBenefits.find(
    (benefit: any) => benefit?.originDestinationInfo?.destination === arrivalAirportIata
  )
  return matchingBenefit?.segmentSupplyBenefitList?.benefitList || []
}

export const getBaggageDataForAdult = (segmentBenefits: any[], baggageType: string) => {
  if (!segmentBenefits || !Array.isArray(segmentBenefits)) {
    return {}
  }

  const benefit = segmentBenefits.find((benefit: any) => benefit?.benefitType === baggageType)
  return benefit?.details?.paxWiseBaggage?.ADT || {}
}

export const getJourneyBenefits = (benefits: any, index: number) => {
  const isSplRt = !isEmpty(benefits?.tripFareBenefits)
  if (index === 0) {
    return isSplRt ? benefits?.tripFareBenefits?.onwardFareBenefits : benefits?.onwardFareBenefits
  } else {
    return isSplRt ? benefits?.tripFareBenefits?.returnFareBenefits : benefits?.returnFareBenefits
  }
}

export const getModifiedFareBenefits = (tripBenefits: any, selectedTab: string) => {
  let newTripBenefits = deepClone(tripBenefits)
  const tripBenefitsList = newTripBenefits?.benefits?.benefitList || []
  const tripBenefitsSummary = newTripBenefits?.benefits?.benefitSummary || []
  let journeyBenefit = newTripBenefits?.[selectedTab] || {}
  const tripBenefitsListKeys: string[] = []
  tripBenefitsList.forEach((tripBenefits: { benefitType: string }) => {
    tripBenefitsListKeys.push(tripBenefits?.benefitType)
  })
  tripBenefitsListKeys.map(key => {
    const tripList = tripBenefitsList?.find((tripBenefit: { benefitType: string }) => tripBenefit?.benefitType === key)
    const tripSummary = tripBenefitsSummary?.find(
      (tripBenefit: { benefitType: string }) => tripBenefit?.benefitType === key
    )
    if (journeyBenefit?.journeyFareBenefits?.benefitList) {
      const index = journeyBenefit.journeyFareBenefits.benefitList.findIndex(
        (benefit: any) => benefit?.benefitType === key
      )
      if (index !== -1) {
        journeyBenefit.journeyFareBenefits.benefitList[index] = tripList
      } else {
        journeyBenefit.journeyFareBenefits.benefitList.push(tripList)
      }
    }
    if (journeyBenefit?.journeyFareBenefits?.benefitSummary) {
      const index = journeyBenefit.journeyFareBenefits.benefitSummary.findIndex(
        (benefit: any) => benefit?.benefitType === key
      )
      if (index !== -1) {
        journeyBenefit.journeyFareBenefits.benefitSummary[index] = tripSummary
      } else {
        journeyBenefit.journeyFareBenefits.benefitSummary.push(tripSummary)
      }
    }
  })
  return journeyBenefit
}

export const getNearbyAirportData = (flightData: any, searchDetails: any, leg: number) => {
  const { searchedFromAirport, searchedFromAirportName, searchedToAirport, searchedToAirportName, journeyType } =
    searchDetails || {}
  const { departureAirportCode: fromAirport, arrivalAirportCode: toAirport } = flightData || {}
  const fromAirportName = flightData?.segments?.[0]?.departAirport || ''
  const toAirportName = flightData?.segments?.[flightData?.segments.length - 1]?.arrivalAirport || ''
  const isRt = journeyType !== 'ONE_WAY'
  const searchFromAirportProcessed = isRt && leg !== 0 ? searchedToAirport : searchedFromAirport
  const searchToAirportProcessed = isRt && leg !== 0 ? searchedFromAirport : searchedToAirport
  const searchFromAirportNameProcessed = isRt && leg !== 0 ? searchedToAirportName : searchedFromAirportName
  const searchToAirportNameProcessed = isRt && leg !== 0 ? searchedFromAirportName : searchedToAirportName
  const isFromNearby = searchFromAirportProcessed && searchFromAirportProcessed !== fromAirport
  const isToNearby = searchToAirportProcessed && searchToAirportProcessed !== toAirport
  const journeyLeg = leg === 0 ? 'onward' : 'return'
  return {
    fromAirportName,
    toAirportName,
    isFromNearby,
    isToNearby,
    journeyLeg,
    searchFromAirportProcessed,
    searchToAirportProcessed,
    searchFromAirportNameProcessed,
    searchToAirportNameProcessed,
    fromAirport,
    toAirport,
    isRt,
  }
}

export const showNearbyAirportCallout = (flightData: any, searchDetails: any) => {
  let isNearby = false
  flightData.forEach((flight: any, index: number) => {
    const { isFromNearby, isToNearby } = getNearbyAirportData(flight, searchDetails, index)
    isNearby = isNearby || isFromNearby || isToNearby
  })
  return isNearby
}

export const isPTCError = (fareCards: any, isFF: boolean, isRT: boolean, isIntl: boolean) => {
  if (!isFF || !isRT || isIntl) {
    return { isError: false, message: '' }
  }

  const { ow, rt } = getSelectedFareCardCfw(fareCards)
  const { isError = false, message = '' } = validatePTC([ow, rt])
  return { isError, message }
}

export const isAvailableAtTrip = (
  selectedFareCard: any,
  type: string
): boolean => {
  const benefitList = selectedFareCard?.tripFareBenefits?.benefits?.benefitList ?? [];
  return benefitList.some((benefit: any) => benefit?.benefitType === type);
};

export const getSplRtFareRules = (
  selectedFareCard: any,
  isCancelAvailableAtTrip: boolean,
  isAmendAvailableAtTrip: boolean
) => {
  const splRtOnwardFareRules: any[] = [];
  const splRtReturnFareRules: any[] = [];

  // Helper to safely get the first benefit of a given type from a benefitList
  const getFirstBenefit = (benefitList: any[] = [], type: string) =>
      benefitList.find((benefit: any) => benefit?.benefitType === type);

  // Helper function to handle both CANCEL and AMEND
  const handleBenefitType = (
      type: string,
      isAvailableAtTrip: boolean
  ) => {
      if (isAvailableAtTrip) {
          const benefit = getFirstBenefit(
              selectedFareCard?.tripFareBenefits?.benefits?.benefitList,
              type
          );
          if (benefit) {
              splRtOnwardFareRules.push(benefit)
              splRtReturnFareRules.push(benefit);
          }
      } else {
          const onwardBenefit = getFirstBenefit(
              selectedFareCard?.tripFareBenefits?.onwardFareBenefits?.journeyFareBenefits?.benefitList,
              type
          );
          if (onwardBenefit) splRtOnwardFareRules.push(onwardBenefit);

          const returnBenefit = getFirstBenefit(
              selectedFareCard?.tripFareBenefits?.returnFareBenefits?.journeyFareBenefits?.benefitList,
              type
          );
          if (returnBenefit) splRtReturnFareRules.push(returnBenefit);
      }
  };

  handleBenefitType('CANCEL', isCancelAvailableAtTrip);
  handleBenefitType('AMEND', isAmendAvailableAtTrip);

  return { splRtOnwardFareRules, splRtReturnFareRules };
};

function deepEqual(obj1: any, obj2: any) {
  if (obj1 === obj2) return true

  if (obj1 == null || obj2 == null) return false

  if (typeof obj1 !== 'object' || typeof obj2 !== 'object') return false

  let keys1 = Object.keys(obj1)
  let keys2 = Object.keys(obj2)

  if (keys1.length !== keys2.length) return false

  for (let key of keys1) {
      if (!keys2.includes(key) || !deepEqual(obj1[key], obj2[key])) return false
  }

  return true
}

function areAllElementsEqual(arr: any) {
  if (arr.length === 0) return true

  for (let i = 1; i < arr.length; i++) {
      if (!deepEqual(arr[i - 1], arr[i])) return false
  }

  return true
}

export const isSegmentLevelBaggageSame = (benefits: any , segments: any) => {
  const segmentBenefitsArray: any[] = []
  segments.forEach((segment: any) => {
    const { arrivalAirportIata = '' } = segment || {}
    const segmentBenefits = getSegmentBenefits(benefits, arrivalAirportIata)
    segmentBenefitsArray.push(segmentBenefits)
  })
  return !areAllElementsEqual(segmentBenefitsArray)

}

export const getRandomNElements = (arr : any[], n: number) => {
  if (arr.length <= n) return arr;
  const shuffled = arr
    .map((value) => ({ value, sort: Math.random() }))
    .sort((a, b) => a.sort - b.sort)
    .map(({ value }) => value);
  return shuffled.slice(0, n);
}

export const getFinalDays = (startDate: string, endDate: string) => {
  if(isEmpty(startDate) || isEmpty(endDate)){
    return 5
  }
  const days = dayjs(endDate)?.diff(dayjs(startDate), 'days')
  return days + 1
}

export const isMultiPaxBooking = (paxTypePremiumDetails: any) => {
  let paxCount = 0
  paxTypePremiumDetails.map(details => {
      paxCount += details.paxCount
  })
  return {isMultiPax: paxCount > 1, paxCount: paxCount}
}

export const isTravelInsuranceSelected = (insurances: any) => {
  return insurances?.insurances?.[0]?.insuranceDetails?.[0]?.selected
}

export const isTravelInsuranceAvailable = (insurances: any) => {
  return insurances?.insurances?.length > 0
}

export const getNoOfDays = (insurance: any) => {
  const insuranceData = insurance?.insurances?.[0]?.insuranceDetails?.[0]
  return getFinalDays(insuranceData?.startDate, insuranceData?.endDate)
}

export const fetchInsuraceTestimonials = (tripType : string) => {
  return tripType === 'intl' 
    ? insuranceTestimonialsData.intl 
    : insuranceTestimonialsData.dom
}

export const isAeroHubItin = (itineraryId: string) => {
  return itineraryId?.substring(0, 4)?.toLowerCase() === 'niah'
}

export const insuranceExpBuckets = (isMeta : boolean) => {
  const config : Record<string, { showTestimonial: boolean, mandatorySelection: boolean, disableContinue: boolean }> = {
    a: { showTestimonial: true, mandatorySelection: true, disableContinue: true },
    b: { showTestimonial: true, mandatorySelection: false, disableContinue: false },
    
  };

  return isMeta ? config.b : config.a;
};

export const getFlexibleBenefitColor = (benefitApplied: string) => {
  if (benefitApplied === INTL_CANCELATION_INSURANCE || benefitApplied === DOM_CANCELATION_INSURANCE) {
    return { isBenefitSelected: true, color :'#5F1AB6' }
  }
  if (benefitApplied === AMEND_INSURANCE) {
    return { isBenefitSelected: true, color :'#09707A'}
}
  return { isBenefitSelected: false, color :'' }
}

export const getPtcFareInfo = (fareCards = []) => {
  const ptcCategory = PTC_FARE_CATEGORY['student']
  let isPtcFareAvl = false
  let isPtcFareSelected = false
  fareCards?.forEach((card: any) => {
    const { fareCategory = '' } = card || {}
    if (fareCategory === ptcCategory) {
      isPtcFareAvl = true
      if (card?.selected) {
        isPtcFareSelected = true
      }
    }
  })
  return { isPtcFareAvl, isPtcFareSelected }
}

export const getPtcCalloutDetails = (fareCards = {}) => {
  const { onwardFareCards = [], returnFareCards = [], tripFareCards = [] } = fareCards || {}
  const { isPtcFareAvl: isOnwardPtcFareAvl, isPtcFareSelected: isOnwardPtcFareSelected } = getPtcFareInfo(onwardFareCards)
  const { isPtcFareAvl: isReturnPtcFareAvl, isPtcFareSelected: isReturnPtcFareSelected } = getPtcFareInfo(returnFareCards)
  const { isPtcFareAvl: isSplRtPtcFareAvl, isPtcFareSelected: isSplRtPtcFareSelected } = getPtcFareInfo(tripFareCards)
  return { isOnwardPtcFareAvl, isOnwardPtcFareSelected, isReturnPtcFareAvl, isReturnPtcFareSelected, isSplRtPtcFareAvl, isSplRtPtcFareSelected }
}

export const getStudentFareStatus = (isPtcFareAvl: boolean, isPtcFareSelected: boolean) => {
  if (isPtcFareSelected) {
    return 'student'
  }
  if (isPtcFareAvl) {
    return 'student_fare_available'
  }
  return 'NA'
}

export const getStudentItineraryRavenValue = (fareCards = {}) => {
  const {
    isOnwardPtcFareAvl,
    isOnwardPtcFareSelected,
    isReturnPtcFareAvl,
    isReturnPtcFareSelected,
    isSplRtPtcFareAvl,
    isSplRtPtcFareSelected,
  } = getPtcCalloutDetails(fareCards || {})
  const { returnFareCards = [], tripFareCards = [] } = fareCards || {}
  if (!isEmpty(tripFareCards)) {
    return getStudentFareStatus(isSplRtPtcFareAvl, isSplRtPtcFareSelected)
  }
  if (!isEmpty(returnFareCards)) {
    return `${getStudentFareStatus(isOnwardPtcFareAvl, isOnwardPtcFareSelected)}, ${getStudentFareStatus(isReturnPtcFareAvl, isReturnPtcFareSelected)}`
  }
  return getStudentFareStatus(isOnwardPtcFareAvl, isOnwardPtcFareSelected)
}

export const getMissingPtcFareType = (fareCards = {}, isRT: boolean, isOnwardPtcFareAvl: boolean, isReturnPtcFareAvl: boolean, isSplRtPtcFareAvl: boolean) => {
  if (!isEmpty(fareCards?.tripFareCards)) {
    return !isSplRtPtcFareAvl ? 'onwardReturn' : ''
  }
  if (!isOnwardPtcFareAvl && !isReturnPtcFareAvl) {
    return 'onwardReturn'
  }
  if (!isOnwardPtcFareAvl) {
    return isRT ? 'onward' : 'onwardReturn'
  }
  if (!isReturnPtcFareAvl) {
    return 'return'
  }
  return ''
}

export const getInstructionsFromFareCards = (fareCards = {}): InstructionsType[] => {
  const { onwardFareCards = [], returnFareCards = [], tripFareCards = [] } = fareCards || {}
  const combinedFareList = !isEmpty(tripFareCards) ? [tripFareCards] : [onwardFareCards, returnFareCards]
  const instructionsList = combinedFareList.map((fareList = []) => {
    const selectedFare = (fareList || [])?.find((fare: any) => fare?.selected) || {}
    if (selectedFare?.fareCategory === PTC_FARE_CATEGORY['student']) {
      return selectedFare?.instructions || {}
    }
    return {}
  })
  return instructionsList
}

export const checkShowPtcNotAvCallout = (searchDetails: any, fareCards = {}, isIntl: boolean, isOnwardPtcFareAvl: boolean, isReturnPtcFareAvl: boolean, isSplRtPtcFareAvl: boolean) => {
  const { searchUrl = '' } = searchDetails || {}
  const isPtc = searchUrl?.includes('sft=student')
  if (!isPtc) {
    return false
  }
  if (isIntl) {
    if (fareCards?.tripFareCards?.length > 0) {
      return !isSplRtPtcFareAvl
    }
    if (fareCards?.returnFareCards?.length > 0) {
      return !isOnwardPtcFareAvl || !isReturnPtcFareAvl
    }
    return !isOnwardPtcFareAvl
  }
  return false
}

